/**
 * Page addresses as error tracking may keep them (GDPR review 19.10.2026, sister of #1356).
 *
 * A code in some paths opens a page without signing in (a join link, a participant's journey, a
 * client view, a shared reading), and the sign-in link carries the email address and its one-time
 * code in the query. Error tracking gets the path with such codes replaced and no query or hash.
 */
const CODE_PATHS: Array<[RegExp, string]> = [
    [/^\/join\/study\/[^/]+/, '/join/study/:code'],
    [/^\/join\/(?!study(\/|$))[^/]+/, '/join/:code'],
    [/^\/spinagate\/r\/[^/]+/, '/spinagate/r/:code'],
    [/^\/(client|present|embed|session|journey|summary)\/[^/]+/, '/$1/:code'],
];

export function telemetrySafePath(pathname: string): string {
    return CODE_PATHS.reduce((path, [pattern, replacement]) => path.replace(pattern, replacement), pathname);
}

/** A full or relative address with codes masked and the query and hash dropped. */
export function telemetrySafeUrl(href: string): string {
    try {
        const absolute = /^[a-z][a-z0-9+.-]*:/i.test(href);
        const url = new URL(href, 'https://artofthinking.ai');
        const path = telemetrySafePath(url.pathname);
        return absolute ? `${url.origin}${path}` : path;
    } catch {
        return href.split(/[?#]/)[0];
    }
}

type ScrubbableEvent = {
    request?: { url?: string; query_string?: unknown; headers?: Record<string, string> };
    transaction?: string;
    tags?: Record<string, unknown>;
    breadcrumbs?: Array<{ data?: Record<string, unknown> }>;
};

/** Scrubs one Sentry event or transaction in place and returns it. */
export function scrubTelemetryEvent<T extends ScrubbableEvent>(event: T): T {
    if (event.request) {
        if (event.request.url) event.request.url = telemetrySafeUrl(event.request.url);
        delete event.request.query_string;
        if (event.request.headers?.Referer) event.request.headers.Referer = telemetrySafeUrl(event.request.headers.Referer);
        if (event.request.headers?.referer) event.request.headers.referer = telemetrySafeUrl(event.request.headers.referer);
    }
    if (typeof event.transaction === 'string' && event.transaction.startsWith('/')) {
        event.transaction = telemetrySafeUrl(event.transaction);
    }
    if (typeof event.tags?.url === 'string') event.tags.url = telemetrySafeUrl(event.tags.url);
    for (const crumb of event.breadcrumbs ?? []) scrubBreadcrumbData(crumb.data);
    return event;
}

/** Navigation and fetch breadcrumbs carry from/to/url addresses. */
export function scrubBreadcrumbData(data: Record<string, unknown> | undefined): void {
    if (!data) return;
    for (const key of ['from', 'to', 'url']) {
        if (typeof data[key] === 'string') data[key] = telemetrySafeUrl(data[key] as string);
    }
}
